/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import React from 'react';
import type { Feature, Geometry, Polygon } from 'geojson';
import type { Id } from '@turf/helpers';
import { FlyToInterpolator, type PickingInfo } from '@deck.gl/core';
import type { RGBAColor } from '../map/deckgl-types';
import { GeoJsonLayer } from '@deck.gl/layers';
import { ipawsChunksSelector } from './selectors';
import { atom } from 'jotai';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { loadable } from '../utils/loadable';
import { IPAWSProperties } from './types';
import { Legend } from '../components/Legend';
import { PathStyleExtension, type PathStyleExtensionProps } from '@deck.gl/extensions';
import type { RGBColor } from '../map/deckgl-types';
import { viewStateAtom } from '../map/atoms';
import { useMatch } from 'react-router-dom';
import { selectedFeatureAtom } from '../Incident/FeaturePopup';

export const selectedAlertAtom = atom<Feature<Polygon, Partial<IPAWSProperties>> | null>(null);

type Props = {
  visible: boolean;
};

export const useIPAWSLayer = (props: Props) => {
  const match = useMatch({ path: '/' });
  const ipawsData = useAtomValue(loadable(ipawsChunksSelector(match != null)));
  const [selectedAlert] = useAtom(selectedFeatureAtom);

  const setViewState = useSetAtom(viewStateAtom);

  const getLineColor = React.useCallback(
    (d: Feature<Geometry, IPAWSProperties> | null): RGBAColor => {
      if (d?.id === selectedAlert?.id) {
        return [0, 0, 255, 255];
      }
      if (d?.properties.event) {
        const eventColor = lookupEventColor(d.properties.event);
        if (eventColor) {
          return [...eventColor, 255] as RGBAColor;
        }
        return [0, 0, 0, 255];
      }
      return [0, 0, 0, 255];
    },
    [selectedAlert]
  );
  const getLineWidth = React.useCallback(
    (d: Feature<Geometry, IPAWSProperties> | null): number => {
      if (d?.id === selectedAlert?.id) {
        return 1000;
      }
      return 1;
    },
    [selectedAlert]
  );

  const handleClick = React.useCallback(
    (info: PickingInfo) => {
      if (props.visible && info.object && info.coordinate) {
        const [longitude, latitude] = info.coordinate;
        setViewState(view => ({
          ...view,
          longitude,
          latitude,
          transitionInterpolator: new FlyToInterpolator({ speed: 3.0 }),
          transitionDuration: 1000,
        }));
      }
    },
    [setViewState, props.visible]
  );

  const handleFillColor = React.useCallback(
    (f: Feature<Geometry, IPAWSProperties>) => getIpawsFillColor(f, selectedAlert?.id ?? null),
    [selectedAlert]
  );

  const isVisible = props.visible;

  const layers = React.useMemo(
    () =>
      ipawsData.state === 'hasData'
        ? ipawsData.data.map(
            (data, index) =>
              new GeoJsonLayer<IPAWSProperties, PathStyleExtensionProps>({
                id: `ipaws-${index}`,
                data: data,
                getFillColor: handleFillColor,
                pickable: isVisible,
                // highlightColor: [255, 255, 0, 255],
                autoHighlight: false,
                // highlightedObjectIndex:
                //   data?.features?.map(f => f.id)?.indexOf(selectedAlert?.id ?? -1) ??
                //   -1,
                visible: isVisible,
                extensions: [new PathStyleExtension({ offset: true })],
                getOffset: 0,
                onClick: handleClick,
                getLineColor,
                getLineWidth,
                lineWidthMinPixels: 1,
                lineWidthScale: 1,
                stroked: isVisible,
                updateTriggers: {
                  highlightedObjectIndex: [selectedAlert?.id],
                  getFillColor: [selectedAlert?.id],
                  getLineWidth: [selectedAlert?.id],
                  getLineColor: [selectedAlert?.id],
                },
              })
          )
        : [],
    [
      handleFillColor,
      handleClick,
      getLineColor,
      getLineWidth,
      ipawsData,
      selectedAlert?.id,
      isVisible,
    ]
  );

  return layers;
};

export const ipawsColorMap: Record<string, RGBColor> = {
  'Blizzard Warning': [253, 68, 3],
  'Hurricane Warning': [216, 0, 49],
  'Fire Weather Watch': [255, 221, 176],
  'Flood Advisory': [0, 254, 127],
  'Flood Warning': [1, 255, 0],
  'Flash Flood Warning': [127, 23, 14],
  'Flood Watch': [74, 137, 92],
  'High Wind Warning': [219, 165, 33],
  'High Wind Watch': [184, 134, 11],
  'Lake Wind Advisory': [204, 181, 145],
  'Lakeshore Flood Advisory': [157, 249, 77],
  'Marine Weather Statement': [254, 218, 184],
  'Red Flag Warning': [255, 21, 148],
  'Severe Thunderstorm Warning': [241, 169, 59],
  'Severe Thunderstorm Watch': [205, 118, 146],
  'Special Weather Statement': [255, 229, 181],
  'Storm Warning': [149, 0, 212],
  'Tropical Storm Warning': [164, 48, 43],
  'Tornado Warning': [250, 1, 2],
  'Tornado Watch': [255, 254, 0],
  'Wind Advisory': [209, 180, 140],
  'Wind Chill Advisory': [174, 238, 239],
  'Winter Storm Warning': [255, 104, 181],
  'Winter Storm Watch': [70, 130, 180],
  'Winter Weather Advisory': [124, 104, 238],
  nifc: [255, 221, 176],
  tornado_rank_5: [181, 9, 9],
  tornado_rank_4: [219, 129, 0],
  tornado_rank_3: [226, 178, 0],
  tornado_rank_2: [143, 195, 201],
  tornado_rank_1: [95, 133, 188],
};

// Own-property lookup, not bare indexing: the event value comes from the IPAWS
// feed, and a name like `constructor` would otherwise resolve up the prototype
// chain to a function that callers spread into a colour tuple or interpolate
// into CSS. An event the palette does not cover is routine -- NWS publishes
// more event values than this map has keys -- so a miss is undefined, exactly
// as it was.
export const lookupEventColor = (event: string): RGBColor | undefined =>
  Object.hasOwn(ipawsColorMap, event) ? ipawsColorMap[event] : undefined;

const getIpawsFillColor = (
  d: Feature<Geometry, IPAWSProperties> | null,
  selectedId: Id | null
): RGBAColor => {
  const alpha = selectedId == null ? 80 : selectedId === d?.id ? 80 : 80;

  if (d?.properties.event) {
    const eventColor = lookupEventColor(d.properties.event);
    if (eventColor) {
      return [...eventColor, alpha];
    }
  }

  return [255, 255, 255, alpha];
};

export const IpawsLegend = () => {
  const ipawsData = useAtomValue(ipawsChunksSelector(true));

  const eventCount = ipawsData.reduce<Record<string, number>>((acc, chunk) => {
    const events = chunk.features.map((f: Feature<Polygon, IPAWSProperties>) => f.properties.event);
    const chunkEvents = events.reduce((chunkAcc: Record<string, number>, event: string) => {
      if (Object.hasOwn(acc, event)) {
        chunkAcc[event]++;
      } else {
        chunkAcc[event] = 1;
      }
      return acc;
    }, acc);

    return chunkEvents;
  }, {});

  return (
    <Legend
      id="ipaws"
      title="IPAWS Events"
      items={Object.keys(eventCount)
        .sort()
        .map(event => ({
          label: event,
          color: `rgb(${lookupEventColor(event)})`,
          value: eventCount[event],
        }))}
    />
  );
};
